import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { AdminLayout } from '../components'
import { Modal, ConfirmModal, Select, Input, Textarea } from '../../../shared/components'
import { AdminFilterTrigger, AdminFilterSidebar } from '../components'
import adminCategoryService, { Category, CategoryFormValues } from '../../../services/adminCategoryService'
import { buildImageUrl } from '../../../config'

const CSS_HANDLES = [
  'adminCategories',
  'categoriesHeader',
  'categoriesTitle',
  'categoriesSubtitle',
  'categoriesActions',
  'addCategoryButton',
  'categoriesTable',
  'tableHeader',
  'tableRow',
  'categoryInfo',
  'categoryName',
  'categoryStatus',
  'categoryActions',
  'editButton',
  'deleteButton',
  'statusToggle',
  'statusActive',
  'statusInactive',
  'modal',
  'modalOverlay',
  'modalContent',
  'modalHeader',
  'modalTitle',
  'modalClose',
  'modalBody',
  'formGroup',
  'formLabel',
  'formInput',
  'formTextarea',
  'formSelect',
  'formRow',
  'modalFooter',
  'cancelButton',
  'saveButton',
  'pagination',
  'paginationButton',
  'paginationInfo',
  'formImagePreview',
  'formImageInput',
  'formImageRemove'
] as const

const AdminCategories: React.FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const [categories, setCategories] = useState<Category[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState<{ page: number; limit: number; total: number; totalPages: number } | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false)
  const [pendingDeletion, setPendingDeletion] = useState<string | null>(null)
  const [pendingStatusToggle, setPendingStatusToggle] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)

  type CategoryFormData = Omit<CategoryFormValues, 'isActive'> & {
    isActive: string
    allowsScrapReturn?: boolean
  }

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors }
  } = useForm<CategoryFormData>({
    defaultValues: {
      name: '',
      description: '',
      isActive: 'true',
      sortOrder: 0,
      allowsScrapReturn: false,
      metaTitle: '',
      metaDescription: ''
    }
  })

  const defaultFilters = { search: '', isActive: undefined as boolean | undefined }

  const [filters, setFilters] = useState<{ search: string; isActive?: boolean }>(() => ({
    ...defaultFilters
  }))
  const [draftFilters, setDraftFilters] = useState<{ search: string; isActive?: boolean }>(() => ({
    ...defaultFilters
  }))

  const itemsPerPage = 20

  const resolveErrorMessage = (err: any, defaultMessage: string): string => {
    if (err?.message) return err.message
    if (err?.response?.data?.message) return err.response.data.message
    if (typeof err?.response?.data === 'string') return err.response.data
    return defaultMessage
  }

  const previousFiltersRef = useRef<string | null>(null)
  const previousPageRef = useRef<number>(1)
  const isLoadingRef = useRef(false)

  const fetchCategories = useCallback(async (page: number, force: boolean = false) => {
    // Serializar filters para comparação
    const filtersKey = JSON.stringify({ search: filters.search.trim(), isActive: filters.isActive })
    
    // Verificar se filters ou page realmente mudaram
    const filtersChanged = previousFiltersRef.current !== filtersKey
    const pageChanged = previousPageRef.current !== page

    // Se não mudou e já está carregando, não fazer nada (a menos que force seja true)
    if (!force && !filtersChanged && !pageChanged && isLoadingRef.current) {
      return
    }

    // Se não mudou nada e já foi carregado, não fazer nada (evita chamadas duplicadas no React Strict Mode)
    // A menos que force seja true
    if (!force && !filtersChanged && !pageChanged && !isLoadingRef.current && previousFiltersRef.current === filtersKey) {
      return
    }

    isLoadingRef.current = true
    previousFiltersRef.current = filtersKey
    previousPageRef.current = page

    setIsFetching(true)
    setError(null)

    try {
      const response = await adminCategoryService.list({
        page,
        limit: itemsPerPage,
        search: filters.search.trim() || undefined,
        isActive: filters.isActive
      })

      setCategories(response.categories)
      setPagination(response.pagination)
      setCurrentPage(response.pagination.page)
      setHasLoaded(true)
    } catch (err) {
      console.error('Erro ao buscar categorias:', err)
      setError(resolveErrorMessage(err, 'Não foi possível carregar as categorias.'))
      setHasLoaded(true)
    } finally {
      setIsFetching(false)
      isLoadingRef.current = false
    }
  }, [filters, itemsPerPage])

  useEffect(() => {
    fetchCategories(1)
  }, [fetchCategories])

  const hasActiveFilters = filters.search.trim() !== '' || filters.isActive !== undefined

  const handleOpenFilters = () => {
    setDraftFilters({ ...filters })
    setIsFilterSidebarOpen(true)
  }

  const handleCloseFilters = () => {
    setIsFilterSidebarOpen(false)
  }

  const handleDraftFilterChange = <Key extends keyof typeof draftFilters>(key: Key, value: (typeof draftFilters)[Key]) => {
    setDraftFilters((prev) => ({
      ...prev,
      [key]: value
    }))
  }

  const handleApplyFilters = () => {
    setFilters({ ...draftFilters })
    setCurrentPage(1)
    setIsFilterSidebarOpen(false)
  }

  const handleClearFilters = () => {
    const reset = { ...defaultFilters }
    setDraftFilters(reset)
    setFilters(reset)
    setCurrentPage(1)
  }

  const handleAddCategory = () => {
    setActionError(null)
    setEditingCategory(null)
    setImageFile(null)
    setIsModalOpen(true)
  }

  const handleEditCategory = (category: Category) => {
    setActionError(null)
    setEditingCategory(category)
    setImageFile(null)
    setIsModalOpen(true)
  }

  const handleDeleteCategory = async (category: Category) => {
    try {
      // Verificar se a categoria tem produtos antes de permitir exclusão
      let productCount = category._count?.products

      // Se não tiver o count na lista, buscar os dados completos
      if (productCount === undefined) {
        const fullCategory = await adminCategoryService.getById(category.id)
        productCount = fullCategory._count?.products || 0
      }

      // Se tiver produtos, mostrar erro e não abrir o modal
      if (productCount !== undefined && productCount > 0) {
        setActionError(
          `Não é possível excluir a categoria "${category.name}" pois ela possui ${productCount} produto(s) associado(s). Remova os produtos antes de excluir a categoria.`
        )
        return
      }

      // Se não tiver produtos, abrir o modal de confirmação
      setCategoryToDelete(category)
      setDeleteConfirmOpen(true)
    } catch (err) {
      console.error('Erro ao verificar categoria:', err)
      setActionError(
        resolveErrorMessage(err, 'Não foi possível verificar a categoria. Tente novamente.')
      )
    }
  }

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return

    setPendingDeletion(categoryToDelete.id)
    setActionError(null)
    setDeleteConfirmOpen(false)

    try {
      await adminCategoryService.remove(categoryToDelete.id)

      const isLastItemOnPage = categories.length === 1 && currentPage > 1
      const nextPage = isLastItemOnPage ? currentPage - 1 : currentPage

      if (isLastItemOnPage) {
        setCurrentPage(nextPage)
      }

      await fetchCategories(nextPage, true)
    } catch (err) {
      console.error('Erro ao excluir categoria:', err)
      setActionError(
        resolveErrorMessage(err, 'Não foi possível excluir a categoria. Tente novamente.')
      )
    } finally {
      setPendingDeletion(null)
      setCategoryToDelete(null)
    }
  }

  const handleToggleStatus = async (categoryId: string, currentStatus: boolean) => {
    setPendingStatusToggle(categoryId)
    setActionError(null)

    try {
      await adminCategoryService.updateStatus(categoryId, !currentStatus)
      await fetchCategories(currentPage, true)
    } catch (err) {
      console.error('Erro ao atualizar status da categoria:', err)
      setActionError(
        resolveErrorMessage(err, 'Não foi possível atualizar o status da categoria.')
      )
    } finally {
      setPendingStatusToggle(null)
    }
  }

  const onSubmitCategory = async (data: CategoryFormData) => {
    setIsSaving(true)
    setActionError(null)

    const formValues: CategoryFormValues = {
      name: data.name,
      description: data.description || undefined,
      isActive: data.isActive === 'true',
      sortOrder: data.sortOrder || 0,
      allowsScrapReturn: data.allowsScrapReturn ?? false,
      metaTitle: data.metaTitle || undefined,
      metaDescription: data.metaDescription || undefined
    }

    try {
      if (editingCategory) {
        await adminCategoryService.update(editingCategory.id, formValues, imageFile ?? undefined)
        await fetchCategories(currentPage, true)
      } else {
        await adminCategoryService.create(formValues, imageFile ?? undefined)
        setCurrentPage(1)
        await fetchCategories(1, true)
      }

      setIsModalOpen(false)
      setEditingCategory(null)
      setImageFile(null)
      if (imageInputRef.current) imageInputRef.current.value = ''
      reset({
        name: '',
        description: '',
        isActive: 'true',
        sortOrder: 0,
        allowsScrapReturn: false,
        metaTitle: '',
        metaDescription: ''
      })
    } catch (err) {
      console.error('Erro ao salvar categoria:', err)
      const message = resolveErrorMessage(
        err,
        'Não foi possível salvar a categoria. Verifique os dados e tente novamente.'
      )
      setActionError(message)
      throw err
    } finally {
      setIsSaving(false)
    }
  }

  // Reset form when editing category changes
  useEffect(() => {
    if (editingCategory) {
      setImageFile(null)
      if (imageInputRef.current) imageInputRef.current.value = ''
      reset({
        name: editingCategory.name || '',
        description: editingCategory.description || '',
        isActive: editingCategory.isActive ? 'true' : 'false',
        sortOrder: editingCategory.sortOrder || 0,
        allowsScrapReturn: editingCategory.allowsScrapReturn ?? false,
        metaTitle: editingCategory.metaTitle || '',
        metaDescription: editingCategory.metaDescription || ''
      })
    } else {
      setImageFile(null)
      if (imageInputRef.current) imageInputRef.current.value = ''
      reset({
        name: '',
        description: '',
        isActive: 'true',
        sortOrder: 0,
        allowsScrapReturn: false,
        metaTitle: '',
        metaDescription: ''
      })
    }
  }, [editingCategory, reset])

  const isApplyDisabled =
    draftFilters.search === filters.search && draftFilters.isActive === filters.isActive

  const totalPages = pagination?.totalPages ?? 1
  const totalItems = pagination?.total ?? categories.length
  const currentPageNumber = pagination?.page ?? currentPage
  const limit = pagination?.limit ?? itemsPerPage
  const startIndex = totalItems === 0 ? 0 : (currentPageNumber - 1) * limit
  const endIndex = totalItems === 0 ? 0 : Math.min(startIndex + categories.length, totalItems)
  const showLoadingOverlay = hasLoaded && isFetching
  const activeFiltersDescriptions = [
    filters.isActive !== undefined ? `status: ${filters.isActive ? 'ativos' : 'inativos'}` : null,
    filters.search.trim() ? `busca: "${filters.search.trim()}"` : null
  ]
    .filter(Boolean)
    .join(' • ')

  const renderSkeleton = () => (
    <AdminLayout>
      <div className="space-y-8">
        <div className="space-y-3">
          <div className="h-8 w-64 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-80 animate-pulse rounded bg-gray-200" />
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-80" />
            <div className="h-10 w-full animate-pulse rounded bg-gray-200 md:w-48" />
          </div>
        </div>

        <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="mb-4 h-6 w-48 animate-pulse rounded bg-gray-200" />
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={`skeleton-row-${index}`} className="h-16 animate-pulse rounded bg-gray-100" />
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  )

  if (!hasLoaded && isFetching) {
    return renderSkeleton()
  }

  if (error && !hasLoaded && !isFetching) {
    return (
      <AdminLayout>
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="space-y-4 text-center">
            <p className="text-gray-600">{error}</p>
            <button
              onClick={() => fetchCategories(1)}
              className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="relative">
        {showLoadingOverlay && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/60">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
          </div>
        )}

        <div className={`${handles.adminCategories} min-h-screen bg-gray-50`}>
          <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
            <div className={`${handles.categoriesHeader}`}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h1 className={`${handles.categoriesTitle} text-3xl font-bold text-gray-900 mb-2`}>
                    Gestão de Categorias
                  </h1>
                  <p className={`${handles.categoriesSubtitle} text-lg text-gray-600`}>
                    Gerencie as categorias do catálogo
                  </p>
                </div>
                <button
                  onClick={handleAddCategory}
                  className={`${handles.addCategoryButton} inline-flex items-center justify-center rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
                >
                  Adicionar Categoria
                </button>
              </div>
            </div>

            {actionError && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                {actionError}
              </div>
            )}

            {error && hasLoaded && !isFetching && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <span>{error}</span>
                  <button
                    onClick={() => fetchCategories(currentPageNumber)}
                    className="inline-flex items-center text-red-700 underline hover:text-red-800"
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
            )}

            <div className={`${handles.categoriesActions} rounded-xl bg-white p-6 shadow-sm`}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="text-sm text-gray-600">
                    Exibindo{' '}
                    <span className="font-semibold text-gray-900">
                      {totalItems === 0 ? 0 : startIndex + 1} - {endIndex}
                    </span>{' '}
                    de{' '}
                    <span className="font-semibold text-gray-900">{totalItems}</span>
                    {' '}categorias
                  </p>
                  {hasActiveFilters && (
                    <p className="mt-1 text-xs text-blue-600">
                      Filtros aplicados: {activeFiltersDescriptions}
                    </p>
                  )}
                </div>
                <AdminFilterTrigger
                  onClick={handleOpenFilters}
                  hasActiveFilters={hasActiveFilters}
                />
              </div>
            </div>

            <div className="rounded-lg bg-white shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className={`${handles.categoriesTable} w-full`}>
                  <thead className={`${handles.tableHeader} bg-gray-50`}>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Nome
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Status
                      </th>
                      <th className="sticky right-0 z-20 bg-gray-50 px-2 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 shadow-[inset_1px_0_0_0_rgb(226,232,240)] w-24">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {categories.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-10 text-center text-sm text-gray-500">
                          Nenhuma categoria encontrada para os filtros selecionados.
                        </td>
                      </tr>
                    ) : (
                      categories.map((category) => (
                        <tr key={category.id} className={`${handles.tableRow} hover:bg-gray-50`}>
                          <td className="whitespace-nowrap px-6 py-4">
                            <div className={`${handles.categoryInfo}`}>
                              <div className={`${handles.categoryName} text-sm font-medium text-gray-900`}>
                                {category.name}
                              </div>
                              {category.description && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {category.description.substring(0, 50)}...
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-6 py-4">
                            <button
                              onClick={() => handleToggleStatus(category.id, category.isActive)}
                              disabled={pendingStatusToggle === category.id}
                              className={`${handles.statusToggle} ${
                                category.isActive
                                  ? `${handles.statusActive} bg-green-100 text-green-800`
                                  : `${handles.statusInactive} bg-red-100 text-red-800`
                              } rounded-full px-3 py-1 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                                category.isActive ? 'focus-visible:ring-green-300' : 'focus-visible:ring-red-300'
                              } disabled:cursor-not-allowed disabled:opacity-60`}
                            >
                              {pendingStatusToggle === category.id
                                ? 'Atualizando...'
                                : category.isActive
                                ? 'Ativo'
                                : 'Inativo'}
                            </button>
                          </td>
                          <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-2 py-4 text-sm font-medium shadow-[inset_1px_0_0_0_rgb(226,232,240)] w-24">
                            <div className={`${handles.categoryActions} flex space-x-2 justify-center`}>
                              <button
                                onClick={() => handleEditCategory(category)}
                                title="Editar categoria"
                                aria-label="Editar categoria"
                                className={`${handles.editButton} inline-flex h-9 w-9 items-center justify-center rounded-md bg-yellow-400 text-gray-900 shadow-sm transition-colors hover:bg-yellow-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2`}
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16.862 3.487a2.1 2.1 0 112.971 2.97L7.5 18.79 3 21l2.21-4.5 11.652-12.013z" />
                                </svg>
                                <span className="sr-only">Editar categoria</span>
                              </button>
                              <button
                                onClick={() => handleDeleteCategory(category)}
                                title="Excluir categoria"
                                aria-label="Excluir categoria"
                                disabled={pendingDeletion === category.id}
                                aria-busy={pendingDeletion === category.id}
                                className={`${handles.deleteButton} inline-flex h-9 w-9 items-center justify-center rounded-md bg-yellow-400 text-gray-900 shadow-sm transition-colors hover:bg-yellow-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60`}
                              >
                                {pendingDeletion === category.id ? (
                                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-700 border-t-transparent" aria-hidden="true" />
                                ) : (
                                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m-4 0h14" />
                                  </svg>
                                )}
                                <span className="sr-only">
                                  {pendingDeletion === category.id ? 'Excluindo categoria' : 'Excluir categoria'}
                                </span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className={`${handles.pagination} bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200`}>
                  <div className={`${handles.paginationInfo} text-sm text-gray-700`}>
                    {totalItems > 0
                      ? `Mostrando ${startIndex + 1} a ${endIndex} de ${totalItems} categorias`
                      : 'Nenhuma categoria encontrada'}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPageNumber === 1}
                      className={`${handles.paginationButton} px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50`}
                    >
                      Anterior
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`${handles.paginationButton} px-3 py-1 border rounded-md ${
                          page === currentPageNumber
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPageNumber === totalPages}
                      className={`${handles.paginationButton} px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50`}
                    >
                      Próximo
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AdminFilterSidebar
        isOpen={isFilterSidebarOpen}
        onClose={handleCloseFilters}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        isApplyDisabled={isApplyDisabled}
        applyLabel="Aplicar filtros"
        clearLabel="Limpar filtros"
      >
        <div className="space-y-6">
          <div>
            <label
              htmlFor="category-filter-search"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Buscar categorias
            </label>
            <Input
              id="category-filter-search"
              type="text"
              value={draftFilters.search}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                handleDraftFilterChange('search', event.target.value)
              }
              placeholder="Nome da categoria"
            />
          </div>

          <div>
            <label
              htmlFor="category-filter-status"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Status
            </label>
            <Select
              id="category-filter-status"
              name="category-filter-status"
              value={draftFilters.isActive === undefined ? 'all' : draftFilters.isActive ? 'active' : 'inactive'}
              onChange={(value) =>
                handleDraftFilterChange(
                  'isActive',
                  value === 'all' ? undefined : value === 'active'
                )
              }
              options={[
                { value: 'all', label: 'Todos os status' },
                { value: 'active', label: 'Ativos' },
                { value: 'inactive', label: 'Inativos' }
              ]}
              placeholder="Todos os status"
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </AdminFilterSidebar>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setEditingCategory(null)
          setImageFile(null)
          if (imageInputRef.current) imageInputRef.current.value = ''
          reset({
            name: '',
            description: '',
            isActive: 'true',
            sortOrder: 0,
            allowsScrapReturn: false,
            metaTitle: '',
            metaDescription: ''
          })
        }}
        title={editingCategory ? 'Editar Categoria' : 'Nova Categoria'}
      >
        <form onSubmit={handleSubmit(onSubmitCategory)} className="space-y-4">
          {actionError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {actionError}
            </div>
          )}

          <div className={`${handles.formGroup}`}>
            <label htmlFor="name" className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
              Nome *
            </label>
            <Input
              type="text"
              id="name"
              {...register('name', {
                required: 'Nome é obrigatório'
              })}
              error={!!errors.name}
              helperText={errors.name?.message}
            />
          </div>

          <div className={`${handles.formGroup}`}>
            <label htmlFor="description" className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
              Descrição
            </label>
            <Textarea
              id="description"
              {...register('description')}
              rows={3}
            />
          </div>

          <div className={`${handles.formGroup}`}>
            <label className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
              Imagem da categoria
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Exibida na página do catálogo. JPEG, PNG ou WEBP até 5MB.
            </p>
            <div className="flex flex-col gap-3">
              {(imageFile || editingCategory?.imageUrl) && (
                <div className={`${handles.formImagePreview} flex items-center gap-3`}>
                  {imageFile ? (
                    <img
                      src={URL.createObjectURL(imageFile)}
                      alt="Preview"
                      className="h-24 w-24 rounded-lg border border-gray-200 object-cover"
                    />
                  ) : editingCategory?.imageUrl ? (
                    <img
                      src={buildImageUrl(editingCategory.imageUrl)}
                      alt={editingCategory.name}
                      className="h-24 w-24 rounded-lg border border-gray-200 object-cover"
                    />
                  ) : null}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-600 truncate">
                      {imageFile ? imageFile.name : 'Imagem atual'}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setImageFile(null)
                        if (imageInputRef.current) imageInputRef.current.value = ''
                      }}
                      className={`${handles.formImageRemove} mt-1 text-sm text-red-600 hover:text-red-700`}
                    >
                      Remover imagem
                    </button>
                  </div>
                </div>
              )}
              <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files?.[0]
                  setImageFile(file ?? null)
                }}
                className={`${handles.formImageInput} block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100`}
              />
            </div>
          </div>

          <div className={`${handles.formRow} grid grid-cols-2 gap-4`}>
            <div className={`${handles.formGroup}`}>
              <label htmlFor="sortOrder" className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
                Ordem
              </label>
              <Input
                type="number"
                id="sortOrder"
                {...register('sortOrder', {
                  valueAsNumber: true,
                  min: { value: 0, message: 'Ordem deve ser maior ou igual a 0' }
                })}
                error={!!errors.sortOrder}
                helperText={errors.sortOrder?.message}
              />
            </div>

            <div className={`${handles.formGroup}`}>
              <label htmlFor="isActive" className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
                Status
              </label>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <Select
                    id="isActive"
                    value={field.value}
                    onChange={field.onChange}
                    options={[
                      { value: 'true', label: 'Ativo' },
                      { value: 'false', label: 'Inativo' }
                    ]}
                    placeholder="Selecione o status"
                    className={`${handles.formSelect} mt-1`}
                  />
                )}
              />
            </div>
          </div>

          <div className={`${handles.formGroup}`}>
            <Controller
              name="allowsScrapReturn"
              control={control}
              render={({ field }) => (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.value ?? false}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    Permitir opção de troca de sucata (Campinas) para produtos desta categoria
                  </span>
                </label>
              )}
            />
          </div>

          <div className={`${handles.formGroup}`}>
            <label htmlFor="metaTitle" className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
              Meta Title (SEO)
            </label>
            <Input
              type="text"
              id="metaTitle"
              {...register('metaTitle', {
                maxLength: { value: 60, message: 'Meta title deve ter no máximo 60 caracteres' }
              })}
              error={!!errors.metaTitle}
              helperText={errors.metaTitle?.message}
            />
          </div>

          <div className={`${handles.formGroup}`}>
            <label htmlFor="metaDescription" className={`${handles.formLabel} block text-sm font-medium text-gray-700`}>
              Meta Description (SEO)
            </label>
            <Textarea
              id="metaDescription"
              {...register('metaDescription', {
                maxLength: { value: 160, message: 'Meta description deve ter no máximo 160 caracteres' }
              })}
              rows={2}
              error={!!errors.metaDescription}
              helperText={errors.metaDescription?.message}
            />
          </div>

          <div className={`${handles.modalFooter} flex justify-end gap-3 pt-4`}>
            <button
              type="button"
              onClick={() => {
                setIsModalOpen(false)
                setEditingCategory(null)
                setImageFile(null)
                if (imageInputRef.current) imageInputRef.current.value = ''
                reset({
                  name: '',
                  description: '',
                  isActive: 'true',
                  sortOrder: 0,
                  allowsScrapReturn: false,
                  metaTitle: '',
                  metaDescription: ''
                })
              }}
              className={`${handles.cancelButton} rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className={`${handles.saveButton} rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50`}
            >
              {isSaving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false)
          setCategoryToDelete(null)
        }}
        onConfirm={confirmDeleteCategory}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja excluir a categoria "${categoryToDelete?.name}"? Esta ação não pode ser desfeita.`}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        confirmButtonClassName="bg-red-600 hover:bg-red-700"
        isLoading={pendingDeletion !== null}
      />
    </AdminLayout>
  )
}

export default AdminCategories
